'use client';

/**
 * useAsk — client hook for the /api/lab/ask stream.
 *
 * Owns the session transcript (last 6 exchanges, component state only) and
 * the SSE parsing. Raw actions from the gateway are validated against the
 * content registries via validateActions before they reach the caller.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { validateActions, type UIAction } from './actions';
import { isProjectSlug } from '../content/projects';
import { isResearchSlug } from '../content/research';
import { isNodeId } from '../content/knowledge';
import { isKnownAgentId } from './agent-resolver';
import { useLang } from '../i18n/client';
import { getDict } from '../i18n/dictionary';

export interface Exchange {
  id: number;
  question: string;
  answer: string;
  actions: UIAction[];
  status: 'pending' | 'streaming' | 'done' | 'error';
  error?: string;
}

const MAX_EXCHANGES = 6;

/** Must match the server cap in /api/lab/ask. */
export const MAX_QUESTION_LEN = 2000;

let nextId = 1;

export interface AskOptions { surface?: 'command' }

export function useAsk(onActions: (actions: UIAction[]) => void, options: AskOptions = {}) {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const onActionsRef = useRef(onActions);
  onActionsRef.current = onActions;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Locale-aware error copy + request language. Kept in refs so the `ask`
  // callback (memoized on `patch` only) always reads the current locale
  // instead of a value captured at first render.
  const lang = useLang();
  const msgs = getDict(lang).ask.msg;
  const langRef = useRef(lang);
  langRef.current = lang;
  const msgsRef = useRef(msgs);
  msgsRef.current = msgs;

  useEffect(() => () => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const patch = useCallback((id: number, fn: (e: Exchange) => Exchange) => {
    setExchanges((prev) => prev.map((e) => (e.id === id ? fn(e) : e)));
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
    setExchanges((prev) =>
      prev.map((e) =>
        e.status === 'pending' || e.status === 'streaming'
          ? { ...e, status: 'done' as const }
          : e
      )
    );
  }, []);

  const ask = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || abortRef.current) return;

      const id = nextId++;

      // Reject over-length client-side so the visitor gets a clear, permanent
      // reason instead of the generic "try again" (which invites a retry that
      // can never succeed against the server's 2000-char cap).
      if (q.length > MAX_QUESTION_LEN) {
        setExchanges((prev) =>
          [
            ...prev,
            { id, question: q, answer: '', actions: [], status: 'error' as const, error: msgsRef.current.tooLong },
          ].slice(-MAX_EXCHANGES)
        );
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      setExchanges((prev) =>
        [...prev, { id, question: q, answer: '', actions: [], status: 'pending' as const }].slice(
          -MAX_EXCHANGES
        )
      );

      // Abort alone does not invalidate a fetch/read promise that already
      // resolved. Gate every later update and UI action by this controller,
      // so reset/cancel cannot deliver an old answer into a new session.
      const isCurrent = () => abortRef.current === controller && !controller.signal.aborted;
      const update = (fn: (exchange: Exchange) => Exchange) => {
        if (isCurrent()) patch(id, fn);
      };
      const fail = (msg: string) => update((e) => ({ ...e, status: 'error', error: msg }));
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      const commandSurface = optionsRef.current.surface === 'command';

      try {
        const res = await fetch('/api/lab/ask', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ message: q, lang: langRef.current, ...(commandSurface ? { surface: 'command' } : {}) }),
          signal: controller.signal,
        });
        if (!isCurrent()) {
          void res.body?.cancel().catch(() => undefined);
          return;
        }

        if (res.status === 503) {
          fail(msgsRef.current.offline);
          return;
        }
        if (res.status === 429) {
          fail(msgsRef.current.rate);
          return;
        }
        if (res.status === 413) {
          fail(msgsRef.current.tooLong);
          return;
        }
        if (!res.ok || !res.body) {
          fail(msgsRef.current.generic);
          return;
        }

        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (!isCurrent()) return;
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          const frames = buf.split('\n\n');
          buf = frames.pop() ?? '';

          for (const frame of frames) {
            for (const line of frame.split('\n')) {
              if (!isCurrent()) return;
              if (!line.startsWith('data:')) continue;
              let ev: { type?: string; text?: string; actions?: unknown; code?: string };
              try {
                ev = JSON.parse(line.slice(5).trim()) as typeof ev;
              } catch {
                continue;
              }
              if (ev.type === 'delta' && typeof ev.text === 'string') {
                const text = ev.text;
                update((e) => ({
                  ...e,
                  answer: e.answer + text,
                  status: 'streaming',
                }));
              } else if (ev.type === 'actions' && !commandSurface) {
                const valid = validateActions(ev.actions, {
                  isProject: isProjectSlug,
                  isResearch: isResearchSlug,
                  isNode: isNodeId,
                  isAgent: isKnownAgentId,
                });
                if (valid.length > 0) {
                  update((e) => ({ ...e, actions: valid }));
                  if (isCurrent()) onActionsRef.current(valid);
                }
              } else if (ev.type === 'done') {
                update((e) => ({ ...e, status: 'done' }));
              } else if (ev.type === 'error') {
                update((e) =>
                  e.answer
                    ? { ...e, status: 'done' }
                    : { ...e, status: 'error', error: msgsRef.current.generic }
                );
              }
            }
          }
        }

        // Stream ended without a done event — settle the exchange.
        update((e) =>
          e.status === 'pending' || e.status === 'streaming'
            ? { ...e, status: e.answer ? 'done' : 'error', error: e.answer ? undefined : msgsRef.current.generic }
            : e
        );
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') fail(msgsRef.current.generic);
      } finally {
        if (reader) {
          if (!isCurrent()) void reader.cancel().catch(() => undefined);
          reader.releaseLock();
        }
        if (abortRef.current === controller) {
          abortRef.current = null;
          setBusy(false);
        }
      }
    },
    [patch]
  );

  const reset = useCallback(() => {
    cancel();
    setExchanges([]);
  }, [cancel]);

  return { exchanges, busy, ask, cancel, reset };
}
